import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { handleApiError, isErrorStatus } from './errorHandler';
import { getTenantLoginUrl } from './tenantRouting';

const axiosServices = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    config.withCredentials = true; 
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response: AxiosResponse) => {
    // Handle successful responses
    if (response.status >= 200 && response.status < 300) {
      return response;
    }

    // Handle non-success status codes
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    (error as any).response = response;
    return Promise.reject(error);
  },
  (error: AxiosError) => {
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      // Handle specific status codes
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          if (!window.location.href.includes('/login') && !window.location.href.includes('verify-otp')) {
            localStorage.removeItem('currentUser');
            const redirectLoginUrl = getTenantLoginUrl();
            window.location.pathname = redirectLoginUrl;
          }
          break;

        case 403:
          // Forbidden - redirect to no permission page
          if (!window.location.href.includes('/no-permission')) {
            window.location.pathname = '/no-permission';
          }
          break;

        case 404:
          // Not found - could redirect to 404 page
          // console.warn('Resource not found:', data);
          break;

        case 409:
          // Conflict - show conflict message
          // console.warn('Resource conflict:', data);
          break;

        case 422:
          // Validation error - show validation details
          // console.warn('Validation error:', data);
          break;

        case 500:
          // Internal server error - could redirect to error page
          if (!window.location.href.includes('/error')) {
            try {
              sessionStorage.setItem('500_error_data', JSON.stringify(data));
            } catch (err) {
              console.error('Error saving 500 details', err);
            }
            window.location.pathname = '/error/500';
          }
          break;

        default:
      }

      // Return the error response data for consistent error handling
      return Promise.reject({
        response: {
          status,
          data: data || { message: `HTTP ${status} error` }
        }
      });

    } else if (error.request) {
      // Request was made but no response received
      return Promise.reject({
        request: error.request,
        message: 'No response received from server'
      });

    } else {
      // Something else happened
      return Promise.reject({
        message: error.message || 'Request setup failed'
      });
    }
  }
);

export default axiosServices;

// GET request fetcher
export const fetcher = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.get(url, { ...config });
    return res.data;
  } catch (error) {
    // Let SWR handle the error
    throw error;
  }
};

// POST request fetcher
export const fetcherPost = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.post(url, config?.data || {}, { ...config });
    return res.data;
  } catch (error) {
    // Let the calling function handle the error
    throw error;
  }
};

export const fetcherUpload = async (
  args: string | [string, (AxiosRequestConfig & { file?: File | null; files?: File[]; method?: string })?]
) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const method = config?.method?.toLowerCase() || 'post';

    const formData = new FormData();
    if (config?.file) {
      formData.append("file", config.file);
    }
    if (config?.files && Array.isArray(config.files)) {
      config.files.forEach((f, index) => {
        formData.append(`files[${index}]`, f);
      });
    }
    if (config?.data) {
      Object.entries(config.data).forEach(([key, value]) => {
        formData.append(key, value as any);
      });
    }

    const res = await axiosServices.request({
      url,
      method,
      data: formData,
      ...config,
      headers: {
        ...(config?.headers || {}),
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};



// PUT request fetcher
export const fetcherPut = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.put(url, config?.data || {}, { ...config });
    return res.data;
  } catch (error) {
    // Let the calling function handle the error
    throw error;
  }
};

// DELETE request fetcher
export const fetcherDelete = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.delete(url, { ...config });
    return res.status;
  } catch (error) {
    // Let the calling function handle the error
    throw error;
  }
};

// PATCH request fetcher
export const fetcherPatch = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.patch(url, config?.data || {}, { ...config });
    return res.data;
  } catch (error) {
    // Let the calling function handle the error
    throw error;
  }
};

// Generic fetcher that can handle any HTTP method
export const fetcherWithMethod = async (method: string, args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.request({
      method: method.toLowerCase(),
      url,
      ...config
    });
    return res.data;
  } catch (error) {
    // Let the calling function handle the error
    throw error;
  }
};

// Enhanced fetcher with error handling
export const fetcherWithErrorHandling = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.get(url, { ...config });
    return res.data;
  } catch (error) {
    // Handle error and show snackbar    
    handleApiError(error);
    throw error;
  }
};

// Enhanced POST fetcher with error handling
export const fetcherPostWithErrorHandling = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.post(url, config?.data || {}, { ...config });
    return res.data;
  } catch (error) {
    // Handle error and show snackbar
    handleApiError(error);
    throw error;
  }
};

//// Added X-TimeZone header for send request
export const fetcherSendRequest = async (args: string | [string, AxiosRequestConfig?]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const method = config?.method?.toLowerCase() || 'post';
    const res = await axiosServices.request({
      method,
      url,
      ...config,
      headers: {
        ...(config?.headers || {})
      },
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};
//// End Added X-TimeZone header for send request